import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class WebAuthnService {
  private readonly CREDENTIAL_KEY = 'shivam_webauthn_credential_id';
  private readonly USER_KEY = 'shivam_webauthn_user';
  private readonly AUTH_TOKEN_KEY = 'shivam_webauthn_auth_token';

  /** Check if browser supports WebAuthn and platform biometric authenticators */
  public async isSupported(): Promise<boolean> {
    if (typeof window === 'undefined' || !window.PublicKeyCredential) {
      return false;
    }
    try {
      return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  }

  /** Check if user previously enrolled biometrics on this device */
  public isEnrolled(): boolean {
    return !!localStorage.getItem(this.CREDENTIAL_KEY) && !!localStorage.getItem(this.USER_KEY);
  }

  public getEnrolledUser(): any {
    const userJson = localStorage.getItem(this.USER_KEY);
    try {
      return userJson ? JSON.parse(userJson) : null;
    } catch {
      return null;
    }
  }

  /**
   * Enroll device biometric (Fingerprint / Face ID / PIN)
   * Call from devotee profile.
   */
  public async enrollBiometrics(user: any, authToken?: string): Promise<boolean> {
    const supported = await this.isSupported();
    if (!supported || !user) {
      return false;
    }

    try {
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const userIdStr = String(user.userId || user.id || user.userName || 'devotee');
      const userIdBuffer = new TextEncoder().encode(userIdStr);

      const displayName = (user.name || (user.firstName ? user.firstName + (user.lastName ? ' ' + user.lastName : '') : '') || user.userName || 'Devotee').trim();
      const userIdentifier = user.phone || user.phoneNo || user.mobileNumber || user.email || user.userName || 'devotee';

      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: 'Shivam Pooja Booking',
          id: window.location.hostname
        },
        user: {
          id: userIdBuffer,
          name: userIdentifier,
          displayName: displayName
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },  // ES256
          { alg: -257, type: 'public-key' } // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          requireResidentKey: false
        },
        timeout: 60000,
        attestation: 'none'
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      }) as PublicKeyCredential;

      if (credential) {
        // Store credential ID (Base64) and associated auth info locally
        const rawId = Array.from(new Uint8Array(credential.rawId))
          .map(b => String.fromCharCode(b))
          .join('');
        const base64Id = btoa(rawId);

        const normalizedUser = {
          ...user,
          id: user.id || user.userId,
          userId: user.userId || user.id,
          name: displayName,
          displayName: displayName,
          accessToken: authToken || user.accessToken
        };

        localStorage.setItem(this.CREDENTIAL_KEY, base64Id);
        localStorage.setItem(this.USER_KEY, JSON.stringify(normalizedUser));
        if (normalizedUser.accessToken) {
          localStorage.setItem(this.AUTH_TOKEN_KEY, normalizedUser.accessToken);
        }
        return true;
      }
      return false;
    } catch (err: any) {
      if (err.name !== 'NotAllowedError') {
        console.error('WebAuthn enrollment error:', err);
      }
      return false;
    }
  }

  /**
   * Authenticate using device biometrics / PIN
   * Returns saved user and auth token upon biometric verification.
   */
  public async authenticateWithBiometrics(): Promise<{ success: boolean; user?: any; token?: string; error?: string }> {
    const supported = await this.isSupported();
    if (!supported || !this.isEnrolled()) {
      return { success: false, error: 'Biometrics not enrolled on this device' };
    }

    try {
      const base64Id = localStorage.getItem(this.CREDENTIAL_KEY);
      if (!base64Id) {
        return { success: false, error: 'No enrolled credential found' };
      }
      const rawIdBinary = atob(base64Id);
      const rawIdBytes = new Uint8Array(rawIdBinary.length);
      for (let i = 0; i < rawIdBinary.length; i++) {
        rawIdBytes[i] = rawIdBinary.charCodeAt(i);
      }

      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: challenge,
        timeout: 60000,
        rpId: window.location.hostname,
        allowCredentials: [{
          id: rawIdBytes,
          type: 'public-key'
        }],
        userVerification: 'required'
      };

      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions
      });

      if (assertion) {
        const user = this.getEnrolledUser();
        const token = localStorage.getItem(this.AUTH_TOKEN_KEY) || user?.accessToken || '';
        return { success: true, user, token };
      }
      return { success: false, error: 'Biometric verification failed' };
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        return { success: false, error: 'Biometric prompt was cancelled' };
      }
      console.error('WebAuthn authentication error:', err);
      return { success: false, error: err.message || 'Biometric authentication failed' };
    }
  }

  /** Clear enrolled biometrics from this device */
  public removeBiometrics(): void {
    localStorage.removeItem(this.CREDENTIAL_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.AUTH_TOKEN_KEY);
  }
}
